/**
 * Bennji Premium Animations JavaScript
 * Handles scroll-triggered animations and premium interactions
 */

(function() {
  'use strict';

  // Check for reduced motion preference
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (prefersReducedMotion) {
    return; // Exit if user prefers reduced motion
  }

  // Intersection Observer for scroll animations
  const observerOptions = {
    threshold: 0.1,
    rootMargin: '0px 0px -50px 0px'
  };

  // Animation types and their initial states
  const animationTypes = {
    'fade-up': {
      initial: { opacity: '0', transform: 'translateY(20px)' },
      final: { opacity: '1', transform: 'translateY(0)' }
    },
    'fade-in': {
      initial: { opacity: '0' },
      final: { opacity: '1' }
    },
    'scale-in': {
      initial: { opacity: '0', transform: 'scale(0.95)' },
      final: { opacity: '1', transform: 'scale(1)' }
    },
    'slide-left': {
      initial: { opacity: '0', transform: 'translateX(20px)' },
      final: { opacity: '1', transform: 'translateX(0)' }
    },
    'slide-right': {
      initial: { opacity: '0', transform: 'translateX(-20px)' },
      final: { opacity: '1', transform: 'translateX(0)' }
    }
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const animationType = entry.target.getAttribute('data-animate') || 'fade-in';
        const animation = animationTypes[animationType] || animationTypes['fade-in'];
        
        // Apply final state
        Object.assign(entry.target.style, animation.final);
        entry.target.classList.add('animated');
        observer.unobserve(entry.target);
      }
    });
  }, observerOptions);

  // Initialize scroll animations
  function initScrollAnimations() {
    const animatedElements = document.querySelectorAll('[data-animate]');
    animatedElements.forEach(el => {
      const animationType = el.getAttribute('data-animate') || 'fade-in';
      const animation = animationTypes[animationType] || animationTypes['fade-in'];
      
      // Apply initial state
      Object.assign(el.style, {
        ...animation.initial,
        transition: 'opacity 0.6s cubic-bezier(0.16, 1, 0.3, 1), transform 0.6s cubic-bezier(0.16, 1, 0.3, 1)'
      });
      
      observer.observe(el);
    });
  }

  // Smooth reveal for staggered animations
  function initStaggerAnimations() {
    const staggerContainers = document.querySelectorAll('[data-stagger]');
    staggerContainers.forEach(container => {
      const children = Array.from(container.children);
      children.forEach((child, index) => {
        child.style.opacity = '0';
        child.style.animationDelay = `${index * 0.1}s`;
      });
      
      const containerObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const children = Array.from(entry.target.children);
            children.forEach(child => {
              child.style.opacity = '1';
            });
            containerObserver.unobserve(entry.target);
          }
        });
      }, observerOptions);
      
      containerObserver.observe(container);
    });
  }

  // Lazy load images with fade-in
  function initLazyImages() {
    const images = document.querySelectorAll('img[loading="lazy"]');
    const imageObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          img.addEventListener('load', () => {
            img.classList.add('loaded');
          });
          if (img.complete) {
            img.classList.add('loaded');
          }
          imageObserver.unobserve(img);
        }
      });
    });

    images.forEach(img => imageObserver.observe(img));
  }

  // Premium button ripple effect
  function initButtonRipples() {
    const buttons = document.querySelectorAll('.button-primary, .button');
    buttons.forEach(button => {
      button.addEventListener('click', function(e) {
        const ripple = document.createElement('span');
        const rect = this.getBoundingClientRect();
        const size = Math.max(rect.width, rect.height);
        const x = e.clientX - rect.left - size / 2;
        const y = e.clientY - rect.top - size / 2;
        
        ripple.style.width = ripple.style.height = size + 'px';
        ripple.style.left = x + 'px';
        ripple.style.top = y + 'px';
        ripple.classList.add('ripple');
        
        this.appendChild(ripple);
        
        setTimeout(() => {
          ripple.remove();
        }, 600);
      });
    });
  }

  // Smooth scroll for anchor links
  function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function(e) {
        const href = this.getAttribute('href');
        if (href === '#') return;
        
        const target = document.querySelector(href);
        if (target) {
          e.preventDefault();
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      });
    });
  }

  // Parallax effect for hero sections (subtle)
  function initParallax() {
    const heroSections = document.querySelectorAll('.hero');
    if (heroSections.length === 0) return;

    let ticking = false;

    function updateParallax() {
      heroSections.forEach(hero => {
        const scrolled = window.pageYOffset;
        const rate = scrolled * 0.3;
        const heroMedia = hero.querySelector('.hero__media');
        if (heroMedia) {
          heroMedia.style.transform = `translateY(${rate}px)`;
        }
      });
      ticking = false;
    }

    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(updateParallax);
        ticking = true;
      }
    });
  }

  // Initialize all premium features
  function init() {
    initScrollAnimations();
    initStaggerAnimations();
    initLazyImages();
    initButtonRipples();
    initSmoothScroll();
    
    // Only init parallax on desktop
    if (window.innerWidth > 768) {
      initParallax();
    }
  }

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize on page transitions (Shopify view transitions)
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:reorder', init);

})();


